import state from '@/state';
import modal from '@/utils/modal.js';
import createEl from '@/utils/createEl';
import callApi from '@/utils/callApi.js';
import { defineAsyncComponent, nextTick, ref, watch } from 'vue';

const PostModal = defineAsyncComponent(async () => {
  return await import(/* webpackChunkName: "stage-2" */'@/widgets/PostModal');
});

let neighbors = [];
const covers = [];
const currentIndex = ref(0);
let openModalMap = new Map();
let stopIndexWatch = null;

const validNeighbor = async (index, loadMore) => {
  if (index < 0) return false;

  if (! neighbors[index] && loadMore) {
    const newNeighbors = await loadMore(index);
    if (Array.isArray(newNeighbors)) {
      neighbors = newNeighbors;
    }
  }
  
  return index < neighbors.length && !! neighbors[index];
}

async function openPostModal (commentId, initialIndex = 0, commentIds = [], loadMore = () => [], overlay = null, indx = 0) {
  const isMobile = window.innerWidth <= 1000;

  let m;
  const commentMoveHandler = async (index) => {
    if (! await validNeighbor(index, loadMore)) {
      return;
    }

    await openPostModal(
      neighbors[index], 
      0, 
      neighbors, 
      loadMore,
      isMobile ? overlay : null,
      isMobile ? index : 0,
    );

    if (! isMobile) {
      m.destroy();
    }
  };

  let showSwipeAnimation = false;
  if (isMobile) {
    if (! overlay) {
      showSwipeAnimation = true;
      openModalMap = new Map();
      overlay = modal.createOverlay();
      overlay.element.classList.add('gmf-modal-scrollable-overlay');
      covers.length = 0;
      if (stopIndexWatch) {
        stopIndexWatch();
      }
      
      currentIndex.value = commentIds.indexOf(commentId);
      indx = currentIndex.value;
      requestAnimationFrame(() => {
        commentMoveHandler(indx+1);
        commentMoveHandler(indx-1);
      });
      stopIndexWatch = watch(currentIndex, async (newVal) => {
        await commentMoveHandler(newVal+1);
        await commentMoveHandler(newVal-1);
      });
      
      overlay.element.addEventListener('scroll', () => {
        const slide = Math.round(overlay.element.scrollLeft / overlay.element.clientWidth);
        if (slide !== currentIndex.value) {
          const direction = slide > currentIndex.value ? -1 : 1;
          currentIndex.value = slide;

          nextTick(() => {
            const c1 = covers[slide + 3 * direction];
            if (c1) {
              c1.innerHTML = '';
              openModalMap.delete(neighbors[slide + 3 * direction]);
            }
          });
        }
      });

      for (let i = 0; i <= indx; i++) {
        const cover = createEl('div', 'gmf-post-modal-cover');
        overlay.element.appendChild(cover);
        covers[i] = cover;
      }
      if (covers[indx]) {
        overlay.element.scrollTo({
          left: covers[indx].offsetWidth * indx,
          behavior: 'instant'
        });
      }
    }
  } else {
    currentIndex.value = commentIds.indexOf(commentId);
    indx = currentIndex.value;

    if (stopIndexWatch) {
      stopIndexWatch();
      stopIndexWatch = null;
    }
  }

  if (isMobile) {
    if (openModalMap.has(commentId)) {
      return;
    }
    openModalMap.set(commentId, true);
  }

  neighbors = commentIds;

  const canMoveForward = ref(false);
  const canMoveBackward = ref(false);

  (async () => {
    canMoveForward.value = await validNeighbor(indx + 1, loadMore);
    canMoveBackward.value = await validNeighbor(indx - 1, loadMore);
  })();
  
  const provide = {
    post: await loadComment(commentId),
    initialIndex,
    neighborIndex: indx,
    moveComment: commentMoveHandler,
    canMoveForward,
    canMoveBackward,
  };
  
  if (! provide.post) {
    console.error('Failed to load comment', commentId);
    return;
  }

  const modalOptions = {
    provide,
    overlay,
  };
  
  if (isMobile) {
    let cover = covers[indx];
    if (! cover) {
      cover = createEl('div', 'gmf-post-modal-cover');
      overlay.element.appendChild(cover);
      covers.push(cover);
    }
    if (showSwipeAnimation) {
      setTimeout(() => {
        cover.classList.add('gmf-swipe-animation');
      }, 500);
    }
    modalOptions.modalParent = cover;
  }
  m = modal.create(PostModal, true, modalOptions);
  m.body.classList.add('gmf-post-modal');
  m.body.setAttribute('data-gmf', ''); // This signals that we are already handling this element (so for example elAttach wont mess with it)
  if (isMobile) {
    const originalDestroy = m.destroy;
    m.destroy = () => {
      openModalMap.delete(commentId);
      originalDestroy();
    };
  }
}

const commentSearchEndpoint = state.commentSearchEndpoint;
async function loadComment(commentId) {
  if (!commentId) {
    return;
  }

  try {
    const query = new URLSearchParams({
      'id:root': commentId,
      translate: state.merchantConfig?.widgets?.translations.mode !== 'translateNothing',
      include: 'author,votes,targets,linkedProducts,pollVotes',
      link: 'all'
    });
    const res = await callApi('GET', commentSearchEndpoint + `?${query.toString()}`);
    return (await res.json()).comments[0];
  } catch (err) {
    console.error('Failed to load comment', err);
  }
}

export default openPostModal;
